import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/overlays";
import { Input } from "@/components/ui/primitives";
import { api } from "@/lib/api";
import type { Accounts } from "@/lib/accounts";

interface AccountProfile {
  user_id: string;
  name: string;
  email: string | null;
  email_verified: boolean;
}

/** Account settings (R6.4). Display name is server-owned (GET/PATCH
 * /api/account/profile); email/password go through supabase-js directly (the
 * backend holds no service-role key). Functional only — the studio chrome is
 * slated for a redesign. */
export function AccountSettingsDialog({
  accs,
  open,
  onOpenChange,
}: {
  accs: Accounts;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [name, setName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    void api
      .request<AccountProfile>("GET", "/api/account/profile")
      .then((p) => {
        setProfile(p);
        setName(p.name);
      })
      .catch((err: unknown) => toast.error("Couldn't load profile", { description: String(err) }));
  }, [open]);

  const saveName = () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    void api
      .request<AccountProfile>("PATCH", "/api/account/profile", { name: name.trim() })
      .then((p) => {
        setProfile(p);
        toast.success("Name updated");
      })
      .catch((err: unknown) => toast.error("Couldn't update name", { description: String(err) }))
      .finally(() => setBusy(false));
  };

  const changeEmail = () => {
    if (!newEmail.trim() || busy || !accs.updateEmail) return;
    setBusy(true);
    void accs
      .updateEmail(newEmail.trim())
      .then(() => {
        toast.success("Check your new address for a confirmation link");
        setNewEmail("");
      })
      .catch((err: unknown) => toast.error("Couldn't change email", { description: String(err) }))
      .finally(() => setBusy(false));
  };

  const changePassword = () => {
    if (newPassword.length < 6 || busy || !accs.updatePassword) return;
    setBusy(true);
    void accs
      .updatePassword(newPassword)
      .then(() => {
        toast.success("Password updated");
        setNewPassword("");
      })
      .catch((err: unknown) => toast.error("Couldn't update password", { description: String(err) }))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Account settings" className="flex w-80 flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-2xs text-muted-foreground">Email</label>
          <div className="text-sm text-muted-foreground">
            {profile?.email ?? "—"}
            {profile && (
              <span className="ml-2 text-2xs text-muted-foreground">
                {profile.email_verified ? "verified" : "unverified"}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-2xs text-muted-foreground">Display name</label>
          <div className="flex gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
            <Button size="sm" variant="outline" disabled={busy || !name.trim()} onClick={saveName}>
              Save
            </Button>
          </div>
        </div>

        {accs.updateEmail && (
          <div className="flex flex-col gap-1">
            <label className="text-2xs text-muted-foreground">Change email</label>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="new@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
              <Button size="sm" variant="outline" disabled={busy || !newEmail.trim()} onClick={changeEmail}>
                Update
              </Button>
            </div>
          </div>
        )}

        {accs.updatePassword && (
          <div className="flex flex-col gap-1">
            <label className="text-2xs text-muted-foreground">Change password</label>
            <div className="flex gap-2">
              <Input
                type="password"
                placeholder="New password"
                autoComplete="new-password"
                value={newPassword}
                minLength={6}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <Button
                size="sm"
                variant="outline"
                disabled={busy || newPassword.length < 6}
                onClick={changePassword}
              >
                Update
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Minimal "set a new password" dialog — reused by the password-recovery
 * landing (R6.3). Controlled; calls `accs.updatePassword` on the CURRENT
 * (recovery) session. */
export function SetPasswordDialog({
  accs,
  open,
  onOpenChange,
}: {
  accs: Accounts;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const save = () => {
    if (password.length < 6 || busy || !accs.updatePassword) return;
    setBusy(true);
    void accs
      .updatePassword(password)
      .then(() => {
        toast.success("Password set — you're signed in");
        onOpenChange(false);
      })
      .catch((err: unknown) => toast.error("Couldn't set password", { description: String(err) }))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Set a new password" className="flex w-80 flex-col gap-3">
        <Input
          type="password"
          placeholder="New password"
          autoComplete="new-password"
          value={password}
          minLength={6}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button size="sm" variant="outline" disabled={busy || password.length < 6} onClick={save}>
          Save password
        </Button>
      </DialogContent>
    </Dialog>
  );
}
